/* The chain CANVAS — fit-to-width, zoom and the minimap (D4, 2026-09-04).
 *
 * The audit's §10 blocker: a real portfolio shape (12 instruments, atlas
 * 92) ran off the right edge of the card with no scroll, no fit and no
 * "+n more"; the co-owned DAG clipped its fifth column (84). The drawing
 * itself is right — it is the FRAME that was missing.
 *
 * The frame ruled in `mocks/chain-canvas-scale.png`:
 *   · the drawing is SCALED to the card's width on load — "fit"; fit never
 *     magnifies (a small chain stays 1:1, it is not blown up);
 *   · when even the smallest fit would be unreadable the card SCROLLS
 *     sideways instead, and a MINIMAP carries the viewport rectangle;
 *   · a zoom control reads − · N % · + with `fit` and `1:1` beside it, and
 *     the keyboard says the same thing: `+` in, `−` out, `0` back to 1:1.
 *
 * Type floor (palette, 4 Sep): nothing on the diagram is below 12 px at
 * 100 %. Zooming out is the officer's own choice of a smaller lens — the
 * minimap and `1:1` are always one click away — so the floor governs the
 * design tokens, not the zoom.
 *
 * PURE and import-free so the bare node runner can prove it — see
 * tests/unit/canvas/chainCanvas.test.mjs. */

export const CANVAS = {
  /** How far out the officer may zoom, and how far in. */
  min: 0.4,
  max: 1.5,
  /** One press of − / + . */
  step: 0.1,
  /** Room kept beside the drawing inside the card. */
  padX: 24,
  /** The minimap's box (bottom-left of the canvas, per the ruled mock). */
  miniW: 120,
  miniH: 60,
  /** The minimap keeps a hairline inset so its rectangle never sits on the
   *  frame. */
  miniPad: 3,
} as const

/** A scale the control can show and the transform can apply — whole
 *  percents, so the label and the drawing never disagree. */
export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1
  const pct = Math.round(scale * 100)
  return Math.min(CANVAS.max, Math.max(CANVAS.min, pct / 100))
}

/** The scale that fits `contentW` into `viewW`. Fit never magnifies (1 is
 *  the ceiling) and never drops below `CANVAS.min` — past that the card
 *  scrolls and the minimap comes up. Rounded DOWN to the percent so the
 *  drawing always clears the edge it is fitted to. */
export function fitScale(contentW: number, viewW: number): number {
  if (contentW <= 0 || viewW <= 0) return 1
  const raw = (viewW - CANVAS.padX) / contentW
  return clampScale(Math.min(1, Math.floor(raw * 100) / 100))
}

/** One press of − (dir −1) or + (dir +1). */
export function zoomTo(scale: number, dir: -1 | 1): number {
  return clampScale(scale + dir * CANVAS.step)
}

/** Does the drawing still run past the card at this scale? Then the card
 *  scrolls sideways and the minimap is shown. Half a pixel of slack keeps
 *  a rounded fit from reading as an overflow. */
export function overflows(contentW: number, viewW: number, scale: number): boolean {
  return contentW * scale > viewW + 0.5
}

/** The drawing's own footprint at a scale — what the scroller measures. */
export function scaledSize(
  contentW: number,
  contentH: number,
  scale: number
): { w: number; h: number } {
  return { w: Math.round(contentW * scale), h: Math.round(contentH * scale) }
}

export interface MiniFit {
  /** minimap px per drawing px */
  k: number
  ox: number
  oy: number
}

/** The minimap's own scale and the drawing's offset inside it — the whole
 *  chain, centred, never cropped. */
export function miniFit(
  contentW: number,
  contentH: number,
  mw: number = CANVAS.miniW,
  mh: number = CANVAS.miniH
): MiniFit {
  const iw = mw - CANVAS.miniPad * 2
  const ih = mh - CANVAS.miniPad * 2
  if (contentW <= 0 || contentH <= 0) return { k: 1, ox: CANVAS.miniPad, oy: CANVAS.miniPad }
  const k = Math.min(iw / contentW, ih / contentH)
  return {
    k,
    ox: CANVAS.miniPad + (iw - contentW * k) / 2,
    oy: CANVAS.miniPad + (ih - contentH * k) / 2,
  }
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** The viewport rectangle inside the minimap: what the card currently
 *  shows, in minimap coordinates, clamped to the drawing's own footprint
 *  so it never hangs outside the frame. */
export function miniViewport(
  view: { scrollLeft: number; scrollTop: number; viewW: number; viewH: number; scale: number },
  content: { w: number; h: number },
  mini: MiniFit
): Rect {
  const scale = view.scale || 1
  const x0 = Math.max(0, view.scrollLeft / scale)
  const y0 = Math.max(0, view.scrollTop / scale)
  const w = Math.min(content.w - x0, view.viewW / scale)
  const h = Math.min(content.h - y0, view.viewH / scale)
  return {
    x: mini.ox + x0 * mini.k,
    y: mini.oy + y0 * mini.k,
    w: Math.max(2, w * mini.k),
    h: Math.max(2, h * mini.k),
  }
}

/** The scroll offsets that centre the drawing on a point the officer
 *  clicked in the minimap (minimap coordinates in, card scroll out). */
export function scrollFromMini(
  point: { x: number; y: number },
  view: { viewW: number; viewH: number; scale: number },
  content: { w: number; h: number },
  mini: MiniFit
): { left: number; top: number } {
  const scale = view.scale || 1
  const cx = (point.x - mini.ox) / (mini.k || 1)
  const cy = (point.y - mini.oy) / (mini.k || 1)
  const left = (cx - view.viewW / scale / 2) * scale
  const top = (cy - view.viewH / scale / 2) * scale
  return {
    left: Math.max(0, Math.min(content.w * scale - view.viewW, left)),
    top: Math.max(0, Math.min(content.h * scale - view.viewH, top)),
  }
}

/** What the caption beside the minimap says — the ruled wording: the whole
 *  chain, or how much of it the card holds. */
export function lensCaption(contentW: number, viewW: number, scale: number): string {
  if (!overflows(contentW, viewW, scale)) return 'whole chain in view'
  const part = Math.max(1, Math.min(99, Math.round((viewW / (contentW * scale)) * 100)))
  return `${part} % of the chain in view`
}

/** The percent the control shows. */
export function pctLabel(scale: number): string {
  return `${Math.round(scale * 100)} %`
}

/** The zoom keys, ruled with the control: `+` (and `=`, the unshifted key)
 *  zooms in, `−` out, `0` returns to 1:1. Anything else is not ours — the
 *  handler must let it through. */
export type ZoomKey = 'in' | 'out' | 'reset' | null

export function zoomKey(key: string): ZoomKey {
  if (key === '+' || key === '=') return 'in'
  if (key === '-' || key === '_') return 'out'
  if (key === '0') return 'reset'
  return null
}
